import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FocusableComponentLayout } from '@noriginmedia/norigin-spatial-navigation'
import { useHybridFocusable } from '../../hooks/useHybridFocusable'
import {
  ComcastErrorType,
  platformStoreDelete,
  platformStoreGet,
  triggerSentryError,
} from '../../../../shared/helpers.js'
import { ErrorBody } from '../../../../shared/PBSError/index'
import PBSError from '../../../../shared/PBSError'
import tts from '../../lib/TTS.js'
import FocusableButtonTS from '../../components/FocusableButton/FocusableButtonTS'
import { cancelTVaultDonation } from '../../actions/tvaultActions.js'
import {
  CancelDonationProps,
  DonationStatus,
  ManageDonationComponentsEnums,
} from './types'
import { useNavigate } from 'react-router'
import { CancelDonationError } from './CancelDonationError'

const COMPONENT_FOCUS_KEY = 'CANCEL_DONATION'
const BACK_BUTTON_FOCUS_KEY = `${COMPONENT_FOCUS_KEY}_BACK_BUTTON`
const CONFIRM_BUTTON_FOCUS_KEY = `${COMPONENT_FOCUS_KEY}_CONFIRM_BUTTON`
const TITLE = 'Are you sure you want to cancel?'
const PARAGRAPH_1 = 'Your tax-deductible donation goes directly to'
const PARAGRAPH_3 =
  'and helps them serve your community through media that educates, inspires, and entertains.'
const PARAGRAPH_2 =
  'In confirming your cancellation, you will no longer support your local station or have access to local member benefits including'
const GO_BACK_TEXT = 'Go Back'
const CONFIRM_CANCELLATION_TEXT = 'Confirm Cancellation'

const CancelDonation = ({
  donationInfo,
  handleButtonEnter: handleButtonEnterFromProps,
}: CancelDonationProps) => {
  const [errorStatus, setErrorStatus] = useState<ErrorBody | null>(null)
  const [keyButtonFocused, setKeyButtonFocused] = useState<string>('')
  const [readWholeScreen, setReadWholeScreen] = useState<boolean>(false)
  const [textButtonFocused, setTextButtonFocused] = useState<string>('')
  const buttonTTS = useMemo(
    () => `Focused on ${textButtonFocused} Button.`,
    [textButtonFocused]
  )
  const { ref } = useHybridFocusable({})
  const navigate = useNavigate()

  useEffect(() => {
    tts.stopRead()

    if (!readWholeScreen && keyButtonFocused === BACK_BUTTON_FOCUS_KEY) {
      tts.addTextToRead(
        `${TITLE} .... ${PARAGRAPH_1}${' '}${
          donationInfo.shortNameStation
        }${' '}${PARAGRAPH_3} .... ${PARAGRAPH_2}${' '}${
          donationInfo.shortNameStation
        }${' Passport.'} .... ${buttonTTS}`
      )
      setReadWholeScreen(true)
    } else {
      tts.addTextToRead(buttonTTS)
    }
  }, [keyButtonFocused, textButtonFocused])

  const handleButtonFocus = useCallback(
    (
      _: FocusableComponentLayout,
      extraProps: { key: string; ttsFocus: string }
    ) => {
      setKeyButtonFocused(extraProps.key)
      setTextButtonFocused(extraProps.ttsFocus)
    },
    [setTextButtonFocused, setKeyButtonFocused]
  )

  const handleButtonEnter = () => {
    fetchCancelDonation()
    if (platformStoreGet('mvault_id')) {
      platformStoreDelete('mvault_id')
    }
  }

  const fetchCancelDonation = async () => {
    try {
      const cancelTVaultDonationResponse = await cancelTVaultDonation(
        donationInfo.callSignStation,
        donationInfo.subscriptionId
      )

      if (cancelTVaultDonationResponse === DonationStatus.canceled) {
        handleButtonEnterFromProps(
          ManageDonationComponentsEnums.CONFIRM_DONATION_CANCELATION
        )
      }
    } catch (error) {
      const pbsError = new PBSError(error, 'CANCEL_DONATION_ERROR')
      const pbsErrorMessage = pbsError.parse()

      setErrorStatus(pbsErrorMessage)

      triggerSentryError(
        'cancelTVaultDonation',
        'ConfirmDonation.tsx',
        {
          errorMessage:
            error.message ||
            `There was a problem processing the donation ${donationInfo.subscriptionId}`,
          errorType: ComcastErrorType.network,
        },
        true
      )
    }
  }

  const handleOkayButtonClick = () => {
    navigate(-1)
  }

  return (
    <>
      {errorStatus ? (
        <CancelDonationError
          error={errorStatus}
          onEnterPress={handleOkayButtonClick}
        />
      ) : (
        <div ref={ref} className="cancellation-donation-screen__cancel">
          <div className="cancellation-donation-screen__cancel__container-title">
            <h1 className="cancellation-donation-screen__cancel__title">
              {TITLE}
            </h1>
          </div>
          <div className="cancellation-donation-screen__cancel__description">
            <p className="cancellation-donation-screen__cancel__message">
              {PARAGRAPH_1}&nbsp;{donationInfo.shortNameStation} {PARAGRAPH_3}
            </p>
            <p className="cancellation-donation-screen__cancel__message">
              {PARAGRAPH_2} {donationInfo.shortNameStation}
              {' Passport.'}
            </p>
            <div className="cancellation-donation-screen__cancel__container-buttons">
              <FocusableButtonTS<{
                key: string
                ttsFocus: string
              }>
                className="cancellation-donation-screen__button"
                focusKey={BACK_BUTTON_FOCUS_KEY}
                onEnterPress={() => navigate(-1)}
                onFocus={handleButtonFocus}
                extraProps={{
                  key: BACK_BUTTON_FOCUS_KEY,
                  ttsFocus: GO_BACK_TEXT,
                }}
                stealFocus={true}
              >
                {GO_BACK_TEXT}
              </FocusableButtonTS>
              <FocusableButtonTS<{
                key: string
                ttsFocus: string
              }>
                className="cancellation-donation-screen__button"
                focusKey={CONFIRM_BUTTON_FOCUS_KEY}
                onEnterPress={handleButtonEnter}
                onFocus={handleButtonFocus}
                extraProps={{
                  key: CONFIRM_BUTTON_FOCUS_KEY,
                  ttsFocus: CONFIRM_CANCELLATION_TEXT,
                }}
              >
                {CONFIRM_CANCELLATION_TEXT}
              </FocusableButtonTS>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default CancelDonation
